import { Component, OnInit } from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterOutlet
} from '@angular/router';
import { filter } from 'rxjs';
import { scrollAppToTop } from './core/helpers/scroll-to-top.helper';
import { HttpClientModule } from '@angular/common/http';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import 'zone.js';
import { CommonModule } from '@angular/common';
import { ToastrModule, ToastrService } from 'ngx-toastr';
import { NgxUiLoaderModule } from 'ngx-ui-loader';
import { environment } from '../environments/environment';
import { ServiceWorkerRegister } from './core/service-worker/serviceWorker';
import { SwUpdate } from '@angular/service-worker';
import Swal from 'sweetalert2';
import { AuthService } from '../services/auth.service';

@Component({
  selector: 'rental-root',
  standalone: true,
  imports: [
    RouterOutlet,
    CommonModule,
    TranslateModule,
    HttpClientModule,
    NgxUiLoaderModule,
    ToastrModule
  ],
  providers: [TranslateService, ToastrService],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  title = 'esqyre-car-rental';
  corporateToken: string = '';

  constructor(
    public translate: TranslateService,
    private router: Router,
    private route: ActivatedRoute,
    private serviceWorker: ServiceWorkerRegister,
    private updates: SwUpdate,
    private _authService: AuthService
  ) {
    translate.addLangs(['en', 'ar']);
    translate.setDefaultLang('en');
    this.serviceWorker.publicApi();
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((res: any) => {
      if (res && res.token) {
        this.corporateToken = res.token;
        sessionStorage.setItem(
          environment.storageKey,
          this.corporateToken ? this.corporateToken : ''
        );
        if (!this._authService.profileDetails.value) {
          this._authService.loadProfile();
        }
      }
    });
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        scrollAppToTop();
        requestAnimationFrame(() => scrollAppToTop());
        this.checkForUpdates();
      });

    this.updates?.versionUpdates.subscribe((evt: { type: any }) => {
      switch (evt.type) {
      case 'VERSION_DETECTED':
        console.log('New version detected');
        break;
      case 'VERSION_READY':
        console.log('New version ready');
        this.showVersionUpdateAlert();
        break;
      case 'VERSION_INSTALLATION_FAILED':
        console.error('Version installation failed');
        break;
      }
    });

    this.updates?.unrecoverable.subscribe((event: any) => {
      if (
        window.confirm(
          'An error occurred that we cannot recover from, please click OK to reload the page.'
        ) === true
      ) {
        window.location.reload();
      }
    });

    this.checkForUpdates();
  }
  
  private async checkForUpdates(): Promise<void> {
    if (this.updates && this.updates.isEnabled) {
      try {
        console.log('Checking for application updates...');
        const updateFound = await this.updates.checkForUpdate();
        console.log(updateFound ? 'A new version is available!' : 'Already on the latest version.');
      } catch (err) {
        console.error('Failed to check for updates:', err);
      }
    }
  }

  private showVersionUpdateAlert(): void {
    const alertTitle = 'New Version Available!';
    const alertText = 'A new version of the application is available. To ensure you have the latest features and improvements, you must refresh the page.';
    
    Swal.fire({
      title: alertTitle,
      html: `
        <div style="text-align: left; margin: 20px 0; background-color: #f7f8fb;">
          <p style="margin-bottom: 15px; color: #666; background: white; padding: 12px; border-radius: 8px; border: 1px solid #d7ddea;">${alertText}</p>
          
          <div style="background-color: white; padding: 15px; border-radius: 8px; margin: 15px 0; border: 1px solid #d7ddea;">
            <h4 style="color: #495057; margin-bottom: 10px; font-size: 16px;"> How to Hard Refresh:</h4>
            <ul style="margin: 0; padding-left: 20px; color: #6c757d;">
              <li style="margin-bottom: 8px;"><strong>Windows/Linux:</strong> Press <kbd style="background-color: #f7f8fb; border: 1px solid #d7ddea; border-radius: 3px; padding: 2px 6px; font-size: 0.85em;">Ctrl + F5</kbd> or <kbd style="background-color: #f7f8fb; border: 1px solid #d7ddea; border-radius: 3px; padding: 2px 6px; font-size: 0.85em;">Ctrl + Shift + R</kbd></li>
              <li style="margin-bottom: 8px;"><strong>Mac:</strong> Press <kbd style="background-color: #f7f8fb; border: 1px solid #d7ddea; border-radius: 3px; padding: 2px 6px; font-size: 0.85em;">Cmd + Shift + R</kbd></li>
              <li style="margin-bottom: 8px;"><strong>Alternative:</strong> Hold <kbd style="background-color: #f7f8fb; border: 1px solid #d7ddea; border-radius: 3px; padding: 2px 6px; font-size: 0.85em;">Shift</kbd> and click the refresh button</li>
            </ul>
          </div>

          <p style="margin-bottom: 15px; color: #666; background: white; padding: 12px; border-radius: 8px; border: 1px solid #d7ddea;"><strong>Tip:</strong> Hard refresh clears the cache and ensures you get the latest version immediately.</p>
        </div>
      `,
      icon: 'info',
      showCancelButton: false,
      confirmButtonText: 'Refresh Now',
      confirmButtonColor: '#223951',
      allowOutsideClick: false,
      allowEscapeKey: false,
      customClass: {
        actions: 'swal2-actions-center',
        popup: 'app-update-popup',
      }
    }).then((result) => {
      if (result.isConfirmed) {
        this.performHardRefresh();
      }
    });
  }

  private performHardRefresh(): void {
    Swal.fire({
      title: 'Updating...',
      text: 'Please wait while we refresh the application.',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    setTimeout(() => {
      if ('caches' in window) {
        caches.keys().then(cacheNames => {
          return Promise.all(
            cacheNames.map(cacheName => {
              return caches.delete(cacheName);
            })
          );
        }).then(() => {
          location.reload();
        });
      } else {
        location.reload();
      }
    }, 1000);
  }
}
